import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, UserRound } from "lucide-react";
import { PlayerAvatar } from "@/components/cs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, type SearchHit } from "@/lib/api";
import { plural } from "@/lib/format";
import { cn } from "@/lib/utils";

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Header search: nickname, SteamID in any form or a Steam profile link. Ctrl/⌘+K opens it. */
export function PlayerSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim(), 250);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const search = useQuery({
    queryKey: ["search", q],
    queryFn: () => api<{ hits: SearchHit[]; direct: boolean }>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: open && q.length >= 2,
    staleTime: 30_000,
  });
  const hits = q.length >= 2 ? search.data?.hits ?? [] : [];
  useEffect(() => setActive(0), [q]);

  const go = (hit: SearchHit) => {
    setOpen(false);
    setText("");
    navigate(`/player/${hit.steamId}`);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Поиск игрока"
        className="flex items-center gap-2 rounded-md border bg-secondary/40 px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground md:w-56">
        <Search className="size-4" />
        <span className="hidden flex-1 text-left md:inline">Поиск игрока…</span>
        <kbd className="hidden rounded border px-1 font-mono text-[10px] md:inline">{MAC ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[12%] max-w-lg translate-y-0 gap-3 p-4">
          <DialogHeader>
            <DialogTitle className="font-display">Поиск игрока</DialogTitle>
            <DialogDescription>Ник, SteamID (7656…, STEAM_0:…, [U:1:…]) или ссылка на профиль Steam.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="m3nur или steamcommunity.com/id/…"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") (e.preventDefault(), setActive((a) => Math.min(a + 1, hits.length - 1)));
              if (e.key === "ArrowUp") (e.preventDefault(), setActive((a) => Math.max(a - 1, 0)));
              if (e.key === "Enter" && hits[active]) go(hits[active]);
            }}
          />
          <div className="max-h-[50vh] overflow-y-auto">
            {q.length < 2 ? (
              <p className="px-1 py-3 text-sm text-muted-foreground">Ищем среди игроков из матчей, которые вам доступны.</p>
            ) : search.isLoading ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
            ) : !hits.length ? (
              <p className="flex items-center gap-2 px-1 py-3 text-sm text-muted-foreground"><UserRound className="size-4" /> Никого не нашли</p>
            ) : (
              <ul className="space-y-1">
                {hits.map((h, i) => (
                  <li key={h.steamId}>
                    <button type="button" onClick={() => go(h)} onMouseEnter={() => setActive(i)}
                      className={cn("flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left", i === active && "bg-secondary")}>
                      <PlayerAvatar name={h.name} src={h.avatar} className="size-9" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{h.name}</span>
                        <span className="num block truncate text-xs text-muted-foreground">
                          {h.matches ? `${h.matches} ${plural(h.matches, ["матч", "матча", "матчей"])}` : "нет общих матчей"}
                          {h.withYou > 0 && <span className="text-primary"> · с вами {h.withYou}</span>}
                          {h.registered && " · на сайте"}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

